/* @flow */
// Ad Studio: the gym's own generated ads, drafts in progress, and how often ad
// breaks play. Talks to /v1/studio on the music service.
import { observable, computed, decorate, flow, runInAction } from 'mobx'

const POLL_MS = 1500

// Timetable rows in week order (Mon to Sun), then by start time
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const byDayAndTime = (rows) => (rows || []).slice().sort((a, b) =>
  (WEEK.indexOf(a.day) - WEEK.indexOf(b.day)) || String(a.time).localeCompare(String(b.time)))

export default class StudioStore {
  api
  userStore
  audioMessageStore
  playlistStore

  ads = []                 // [{ id, title, status, step, voice, duration, until, uuid }]
  website = ''
  frequency = 'normal'     // light | normal | busy
  made = 0                 // ads the gym has now (drafts, live, ended)
  limit = 10               // ads a gym can have at once; deleting frees a slot
  draft = null             // the ad being created/reviewed
  screen = 'list'          // list | create | making | review
  error = ''
  justWentLive = ''
  loaded = false

  // Class announcements (from the gym's timetable)
  classes = null           // { url, enabled, lead, voice, approved, status, progress, error, classes: [] }
  timetable = []           // the list being edited on the timetable screen
  timetableUrl = ''
  reading = false
  savingClasses = false
  classesScreen = 'summary' // summary | timetable
  classesError = ''
  classesJustReady = false

  pollTimer = null
  classesTimer = null
  listTimer = null

  // Ad pack: a website / Google Doc / text read into a list of proposed ads
  hidden = []              // catalogue ids of deleted ads (never shown as stock)
  reviewDrafts = []        // full drafts shown on the Review all screen
  reviewTimer = null
  rerecording = false
  pack = null               // { status: reading | done | failed, source, suggestions: [{ id, title, brief, until }], error }
  packError = ''
  packBusy = false
  packTimer = null
  approvingAll = false

  constructor (api, userStore, audioMessageStore, playlistStore) {
    this.api = api
    this.userStore = userStore
    this.audioMessageStore = audioMessageStore
    this.playlistStore = playlistStore
  }

  call (method, path, data) {
    return this.api.studio(this.userStore.idToken, method, path, data)
  }

  get canUse () {
    return !!(this.userStore.isAuthenticated && this.userStore.idToken)
  }

  // Studio info for a catalogue message (by its uuid), if the gym made it here
  infoFor (uuid) {
    return this.ads.find((a) => a.uuid && a.uuid === uuid)
  }

  get drafts () {
    return this.ads.filter((a) => a.status === 'draft')
  }

  // Ads of the gym's own that are switched on (for the Studio tab badge)
  get liveCount () {
    const all = this.audioMessageStore.audioMessages || []
    return all.filter((m) => m.enabled && this.infoFor(m.uuid)).length
  }

  load = flow(function * () {
    if (!this.canUse) return
    try {
      const res = yield this.call('get', '/ads')
      this.ads = res.ads || []
      this.hidden = res.hidden || []
      this.website = res.website || ''
      this.frequency = res.frequency || 'normal'
      this.made = res.made || 0
      this.limit = res.limit || 10
      this.playlistStore.adFrequency = this.frequency
      this.loaded = true
    } catch (err) {
      // Panel still works with the existing promotions
      this.loaded = true
    }
  })

  // ---- class announcements
  applyClasses (res) {
    // Tell the gym when recording has just finished
    if (this.classes && this.classes.status === 'recording' && res && res.status === 'ready') {
      this.classesJustReady = true
      setTimeout(() => runInAction(() => { this.classesJustReady = false }), 20000)
    }
    this.classes = res
    this.playlistStore.classAnnouncer.setConfig(res)
    // While announcements are being recorded, check back every few seconds;
    // otherwise refresh every 2 hours (the audio links last 6 hours).
    if (this.classesTimer) clearTimeout(this.classesTimer)
    const wait = res && res.status === 'recording' ? 4000 : 2 * 60 * 60 * 1000
    this.classesTimer = setTimeout(() => this.loadClasses(), wait)
  }

  loadClasses = flow(function * () {
    if (!this.canUse) return
    try {
      const res = yield this.call('get', '/classes')
      this.applyClasses(res)
    } catch (err) {
      // keep what we had; try again later
      if (this.classesTimer) clearTimeout(this.classesTimer)
      this.classesTimer = setTimeout(() => this.loadClasses(), 10 * 60 * 1000)
    }
  })

  openTimetable () {
    this.classesError = ''
    const c = this.classes || {}
    this.timetable = byDayAndTime((c.classes || []).map((k) => ({ id: k.id, day: k.day, time: k.time, name: k.name, room: k.room || '' })))
    this.timetableUrl = c.url || this.website || ''
    this.classesScreen = 'timetable'
  }

  closeTimetable () {
    this.classesError = ''
    this.classesScreen = 'summary'
  }

  readTimetable = flow(function * (url) {
    this.classesError = ''
    this.reading = true
    try {
      yield this.call('post', '/classes/read', { url })
      // Booking widgets are opened in a browser on our side: this can take a minute
      const started = Date.now()
      let job = { status: 'reading' }
      while (job.status === 'reading' && Date.now() - started < 4 * 60 * 1000) {
        yield new Promise((resolve) => setTimeout(resolve, 3000))
        try { job = yield this.call('get', '/classes/read') } catch (e) { /* keep waiting */ }
      }
      if (job.status === 'done') {
        this.timetable = byDayAndTime(job.classes)
        this.timetableUrl = job.url || url
      } else {
        this.classesError = job.error || 'Reading the timetable took too long. Please try again, or add your classes by hand.'
      }
    } catch (err) {
      this.classesError = err.message
    }
    this.reading = false
  })

  // Save the list (approve: record the announcements) or just change a setting
  saveClasses = flow(function * (changes) {
    this.classesError = ''
    this.savingClasses = true
    try {
      const res = yield this.call('put', '/classes', changes)
      this.applyClasses(res)
      if (changes.approve) this.classesScreen = 'summary'
    } catch (err) {
      this.classesError = err.message
    }
    this.savingClasses = false
  })

  setFrequency = flow(function * (frequency) {
    const previous = this.frequency
    this.frequency = frequency
    this.playlistStore.adFrequency = frequency
    try {
      yield this.call('put', '/settings', { frequency })
    } catch (err) {
      this.frequency = previous
      this.playlistStore.adFrequency = previous
      this.error = err.message
    }
  })

  openCreate () {
    this.error = ''
    this.justWentLive = ''
    this.screen = 'create'
  }

  backToList () {
    this.stopPolling()
    this.stopPackPolling()
    this.stopReviewPolling()
    this.error = ''
    this.screen = 'list'
    this.draft = null
    this.load()
  }

  openDraft = flow(function * (id) {
    this.error = ''
    this.justWentLive = ''
    try {
      const d = yield this.call('get', `/ads/${id}`)
      this.draft = d
      this.screen = d.step === 'ready' || d.step === 'failed' ? 'review' : 'making'
      if (this.screen === 'making') this.startPolling()
    } catch (err) {
      this.error = err.message
    }
  })

  create = flow(function * (form) {
    this.error = ''
    try {
      const d = yield this.call('post', '/ads', form)
      this.draft = d
      this.screen = 'making'
      this.startPolling()
    } catch (err) {
      this.error = err.message
    }
  })

  rerecord = flow(function * (script, voice, mood, title) {
    if (!this.draft) return
    this.error = ''
    try {
      const d = yield this.call('post', `/ads/${this.draft.id}/rerecord`, { script, voice, mood, title })
      this.draft = d
      this.screen = 'making'
      this.startPolling()
    } catch (err) {
      this.error = err.message
    }
  })

  regenerate = flow(function * () {
    if (!this.draft) return
    this.error = ''
    try {
      const d = yield this.call('post', `/ads/${this.draft.id}/regenerate`)
      this.draft = d
      this.screen = 'making'
      this.startPolling()
    } catch (err) {
      this.error = err.message
    }
  })

  approve = flow(function * () {
    if (!this.draft) return
    this.error = ''
    try {
      const d = yield this.call('post', `/ads/${this.draft.id}/approve`)
      this.justWentLive = d.title || 'Your ad'
      this.draft = null
      this.screen = 'list'
      yield this.load()
      // The catalogue takes a minute or so to pick the file up: check every
      // few seconds (for up to 3 minutes) until it's in the gym's messages.
      this.waitForCatalogue(d.uuid)
    } catch (err) {
      this.error = err.message
    }
  })

  archive = flow(function * (id) {
    try {
      yield this.call('post', `/ads/${id}/archive`)
      yield this.load()
      this.audioMessageStore.loadAudioMessages()
    } catch (err) {
      this.error = err.message
    }
  })

  waitForCatalogue (uuid) {
    if (!uuid) return
    const started = Date.now()
    const check = async () => {
      try { await this.audioMessageStore.loadAudioMessages() } catch (e) {}
      const found = (this.audioMessageStore.audioMessages || []).some((m) => m.uuid === uuid)
      if (!found && Date.now() - started < 180000) setTimeout(check, 6000)
    }
    setTimeout(check, 4000)
  }

  // ---- Ad pack
  openPack () {
    this.error = ''
    this.packError = ''
    this.justWentLive = ''
    this.screen = 'pack'
    this.loadPack()
  }

  // Opening the screen starts fresh; only a read still in progress is picked up again
  loadPack = flow(function * () {
    this.pack = null
    try {
      const p = yield this.call('get', '/pack')
      if (p && p.status === 'reading') {
        this.pack = p
        this.startPackPolling()
      }
    } catch (err) { /* no pack yet */ }
  })

  readPack = flow(function * (source) {
    this.packError = ''
    this.packBusy = true
    try {
      const p = yield this.call('post', '/pack/read', source)
      this.pack = p
      this.startPackPolling()
    } catch (err) {
      this.packError = err.message
    }
    this.packBusy = false
  })

  startPackPolling () {
    this.stopPackPolling()
    this.packTimer = setInterval(async () => {
      try {
        const p = await this.call('get', '/pack')
        runInAction(() => {
          this.pack = p
          if (!p || p.status !== 'reading') this.stopPackPolling()
        })
      } catch (err) { /* keep polling */ }
    }, 3000)
  }

  stopPackPolling () {
    if (this.packTimer) clearInterval(this.packTimer)
    this.packTimer = null
  }

  // Make the ticked suggestions as drafts, then go back to the list to watch them being made
  makePack = flow(function * (items, voice, mood, website) {
    this.packError = ''
    this.packBusy = true
    try {
      yield this.call('post', '/pack/make', { items, voice, mood, website })
      this.pack = null
      this.screen = 'list'
      yield this.load()
      this.startListPolling()
    } catch (err) {
      this.packError = err.message
    }
    this.packBusy = false
  })

  // While drafts are being made, refresh the list every few seconds
  startListPolling () {
    this.stopListPolling()
    this.listTimer = setInterval(async () => {
      try {
        await this.load()
        const making = this.ads.some((a) => a.status === 'draft' && a.step !== 'ready' && a.step !== 'failed')
        if (!making) this.stopListPolling()
      } catch (err) { /* keep polling */ }
    }, 5000)
  }

  stopListPolling () {
    if (this.listTimer) clearInterval(this.listTimer)
    this.listTimer = null
  }

  get readyDrafts () {
    return this.ads.filter((a) => a.status === 'draft' && a.step === 'ready')
  }

  approveAll = flow(function * () {
    this.error = ''
    this.approvingAll = true
    try {
      const r = yield this.call('post', '/ads/approve-all')
      this.justWentLive = r && r.approved ? `${r.approved} ad${r.approved === 1 ? '' : 's'}` : ''
      this.screen = 'list'
      this.reviewDrafts = []
      yield this.load()
      this.waitForCatalogueAll()
    } catch (err) {
      this.error = err.message
    }
    this.approvingAll = false
  })

  // Approve one draft from the Review all screen and stay there
  approveOne = flow(function * (id) {
    this.error = ''
    try {
      const d = yield this.call('post', `/ads/${id}/approve`)
      this.reviewDrafts = this.reviewDrafts.filter((x) => x.id !== id)
      yield this.load()
      this.waitForCatalogue(d.uuid)
      if (!this.reviewDrafts.length) this.screen = 'list'
    } catch (err) {
      this.error = err.message
    }
  })

  // Every live ad of the gym's that the catalogue hasn't listed yet: keep
  // refreshing the messages until they all appear (up to 3 minutes), so the
  // on/off switches and the counts come alive without a reload.
  waitForCatalogueAll () {
    const started = Date.now()
    const check = async () => {
      try { await this.audioMessageStore.loadAudioMessages() } catch (e) {}
      const have = this.audioMessageStore.audioMessages || []
      const missing = this.ads.some((a) => a.status === 'live' && a.uuid && !have.some((m) => m.uuid === a.uuid))
      if (missing && Date.now() - started < 180000) setTimeout(check, 6000)
    }
    setTimeout(check, 4000)
  }

  // ---- Review all drafts on one screen (listen, fix wording, re-record, approve)
  openReviewAll = flow(function * () {
    this.error = ''
    this.justWentLive = ''
    this.screen = 'reviewAll'
    this.reviewDrafts = []
    yield this.loadReviewDrafts()
    this.startReviewPolling()
  })

  // Full details (script, audio) for each draft: the list only carries the basics
  loadReviewDrafts = flow(function * () {
    const ids = this.drafts.map((d) => d.id)
    const out = []
    for (const id of ids) {
      try { out.push(yield this.call('get', `/ads/${id}`)) } catch (e) { /* skip */ }
    }
    // Keep the newest first, as the list does
    this.reviewDrafts = out
  })

  // While any draft is still being recorded, refresh every few seconds
  startReviewPolling () {
    this.stopReviewPolling()
    this.reviewTimer = setInterval(async () => {
      if (this.screen !== 'reviewAll') return this.stopReviewPolling()
      const busy = this.reviewDrafts.filter((d) => d.step !== 'ready' && d.step !== 'failed')
      if (!busy.length) return
      for (const d of busy) {
        try {
          const fresh = await this.call('get', `/ads/${d.id}`)
          runInAction(() => { this.reviewDrafts = this.reviewDrafts.map((x) => x.id === fresh.id ? fresh : x) })
        } catch (e) { /* try again next time */ }
      }
    }, 4000)
  }

  stopReviewPolling () {
    if (this.reviewTimer) clearInterval(this.reviewTimer)
    this.reviewTimer = null
  }

  // Re-record every draft whose wording, name, voice or music was changed, one after another
  rerecordMany = flow(function * (changes) {
    this.error = ''
    this.rerecording = true
    for (const c of changes) {
      try {
        const d = yield this.call('post', `/ads/${c.id}/rerecord`, { script: c.script, voice: c.voice, mood: c.mood, title: c.title })
        this.reviewDrafts = this.reviewDrafts.map((x) => x.id === d.id ? d : x)
      } catch (err) {
        this.error = err.message
      }
    }
    this.rerecording = false
    yield this.load()
    this.startReviewPolling()
  })

  // Delete a draft from the Review all screen
  archiveFromReview = flow(function * (id) {
    this.reviewDrafts = this.reviewDrafts.filter((x) => x.id !== id)
    yield this.archive(id)
    if (!this.reviewDrafts.length) this.screen = 'list'
  })

  startPolling () {
    this.stopPolling()
    this.pollTimer = setInterval(async () => {
      if (!this.draft) return this.stopPolling()
      try {
        const d = await this.call('get', `/ads/${this.draft.id}`)
        runInAction(() => {
          this.draft = d
          if (d.step === 'ready' || d.step === 'failed') {
            this.stopPolling()
            this.screen = 'review'
          }
        })
      } catch (err) { /* keep polling */ }
    }, POLL_MS)
  }

  stopPolling () {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
  }
}

decorate(StudioStore, {
  ads: observable,
  made: observable,
  limit: observable,
  website: observable,
  frequency: observable,
  draft: observable.ref,
  screen: observable,
  error: observable,
  justWentLive: observable,
  loaded: observable,
  classes: observable.ref,
  timetable: observable.ref,
  timetableUrl: observable,
  reading: observable,
  savingClasses: observable,
  classesScreen: observable,
  classesError: observable,
  classesJustReady: observable,
  pack: observable.ref,
  packError: observable,
  packBusy: observable,
  approvingAll: observable,
  reviewDrafts: observable.ref,
  hidden: observable.ref,
  rerecording: observable,
  readyDrafts: computed,
  drafts: computed,
  liveCount: computed,
  canUse: computed
})
